import { useAsync } from 'react-use';

import { GroupEntity } from '@backstage/catalog-model';
import {
  identityApiRef,
  useApi,
  discoveryApiRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

interface ManagedTeamInfo {
  teamRef: string;
  teamName: string;
  allTeamRefs: string[]; // includes the main team + all children
}

/**
 * Normalizes a team reference to the full format: 'group:default/teamname'
 * Handles three formats:
 * 1. Just name: 'sup0002445' -> 'group:default/sup0002445'
 * 2. Full ref: 'group:default/sup0002445' -> 'group:default/sup0002445'
 * 3. Partial ref: 'group:sup0002445' -> 'group:default/sup0002445'
 */
export function normalizeTeamRef(ref: string): string {
  const trimmed = ref.trim();

  if (trimmed.startsWith('group:default/')) {
    return trimmed;
  }

  if (trimmed.startsWith('group:')) {
    return trimmed.replace('group:', 'group:default/');
  }

  return `group:default/${trimmed}`;
}

/**
 * Finds the top-level managed team from a list of teams managed by the user.
 * A top-level team is one whose parent is NOT also managed by the user.
 *
 * Examples:
 * - User manages [TeamA, TeamB], TeamB.parent = TeamA -> Returns TeamA
 * - User manages [TeamA], TeamA.parent = TeamC (not managed) -> Returns TeamA
 * - User manages [TeamA, TeamB], both have different parents -> Returns TeamA (first)
 */
export function findTopLevelManagedTeam(
  managedTeams: GroupEntity[],
): GroupEntity {
  if (managedTeams.length === 0) {
    throw new Error('Cannot find top-level team from empty array');
  }

  if (managedTeams.length === 1) {
    return managedTeams[0];
  }

  // Build set of all managed team refs for quick lookup
  const managedTeamRefs = new Set(
    managedTeams.map(t => normalizeTeamRef(t.metadata.name)),
  );

  // Find team whose parent is NOT in the managed set
  const topLevelTeam = managedTeams.find(team => {
    const parentRef = team.spec?.parent as string | undefined;

    if (!parentRef) {
      // No parent = top-level
      return true;
    }

    const normalizedParent = normalizeTeamRef(parentRef);
    return !managedTeamRefs.has(normalizedParent);
  });

  // Fallback to first team if all have managed parents (shouldn't happen normally)
  return topLevelTeam ?? managedTeams[0];
}

/**
 * Checks if a user is a manager of a given team.
 * Compares user email (case-insensitive) with team's managers list.
 */
export function isUserManagerOfTeam(
  team: GroupEntity,
  userEmail: string,
): boolean {
  if (!team?.metadata?.managers) {
    return false;
  }

  const managers = team.metadata.managers as Array<{
    email: string;
    name?: string;
  }>;

  return managers.some(
    manager => manager.email?.toLowerCase() === userEmail.toLowerCase(),
  );
}

/**
 * Fetches team hierarchy from the team-tree API.
 *
 * @param teamName - The team name to fetch hierarchy for
 * @param discoveryApi - Discovery API for finding the service URL
 * @param token - Authentication token
 * @returns Array of all team IDs (normalized format) including the root team
 */
async function fetchTeamTreeFromAPI(
  teamName: string,
  discoveryApi: any,
  token: string,
): Promise<string[]> {
  try {
    const baseUrl = await discoveryApi.getBaseUrl('common-utilities');
    const response = await fetch(`${baseUrl}/team-tree/${teamName}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch tree for ${teamName}`);
    }

    const data = await response.json();
    // Convert team IDs to full refs
    return data.flatTree.map((teamId: string) => normalizeTeamRef(teamId));
  } catch (error) {
    // Fallback to empty array on error
    return [];
  }
}

/**
 * Custom hook that finds the user's top-level managed team and all its descendants.
 *
 * Logic:
 * 1. Gets user's email from identity API
 * 2. Gets all teams user is a member of
 * 3. Filters to teams where user is manager (in metadata.managers)
 * 4. Finds the top-level managed team (parent not also managed)
 * 5. Fetches team hierarchy from team-tree API
 *
 * @returns {ManagedTeamInfo | null} - Top-level team info with all descendants, or null
 */
export const useManagedTeamWithChildren = () => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const {
    value: managedTeamInfo,
    loading: managedTeamLoading,
    error: managedTeamError,
  } = useAsync(async (): Promise<ManagedTeamInfo | null> => {
    // Step 1: Get user email and token
    const { userEntityRef } = await identityApi.getBackstageIdentity();
    const { token } = await identityApi.getCredentials();
    const userEntity = await catalogApi.getEntityByRef(userEntityRef);

    const profile = userEntity?.spec?.profile as { email?: string } | undefined;
    const userEmail = profile?.email;

    if (!userEmail) {
      return null;
    }

    // Step 2: Get all teams user is a member of
    const groupRefs =
      userEntity?.relations
        ?.filter(r => r.type === 'memberOf' && r.targetRef.startsWith('group:'))
        .map(r => r.targetRef) || [];

    if (groupRefs.length === 0) {
      return null;
    }

    // Step 3: Fetch all group entities (in parallel)
    const groupEntities = (
      await Promise.all(
        groupRefs.map(async ref => {
          try {
            return await catalogApi.getEntityByRef(ref);
          } catch {
            return null;
          }
        }),
      )
    ).filter((group): group is GroupEntity => group !== null);

    // Step 4: Filter to teams where user is manager
    const managedTeams = groupEntities.filter(group =>
      isUserManagerOfTeam(group, userEmail),
    );

    if (managedTeams.length === 0) {
      return null;
    }

    // Step 5: Find top-level managed team (for backwards compatibility)
    const topLevelTeam = findTopLevelManagedTeam(managedTeams);
    const teamRef = normalizeTeamRef(topLevelTeam.metadata.name);

    if (!token) {
      // Fallback if token is not available
      return {
        teamRef,
        teamName: topLevelTeam.metadata.name,
        allTeamRefs: [teamRef],
      };
    }

    // Step 6: Fetch team hierarchy from team-tree API for ALL managed teams
    // This ensures we get all teams + children even if user manages multiple unrelated teams
    const allTeamRefsArrays = await Promise.all(
      managedTeams.map(team =>
        fetchTeamTreeFromAPI(team.metadata.name, discoveryApi, token),
      ),
    );

    // Flatten and deduplicate all team refs
    const allTeamRefs = Array.from(new Set(allTeamRefsArrays.flat()));

    return {
      teamRef,
      teamName: topLevelTeam.metadata.name,
      allTeamRefs: allTeamRefs.length > 0 ? allTeamRefs : [teamRef],
    };
  }, [identityApi, catalogApi, discoveryApi]);

  return { managedTeamInfo, managedTeamLoading, managedTeamError };
};
